/**
 * useComposeForm Hook
 * Manages form state, validation, and field status for compose dialog
 * Supports both authenticated and guest users
 */

import { useState, useCallback } from 'react';
import { z } from 'zod';
import { useLocalizations } from '@/hooks/useLocalizations';
import type { ComposeFormData, Expert } from '../types';
import type { FieldStatus } from '@/components/FormValidation';

// Input validation schema - only recipients and message are required
const messageSchema = z.object({
  recipients: z.array(z.string()).min(1, "validation.select_recipient").max(3, "validation.max_recipients"),
  subject: z.string().trim().max(200, "validation.subject_max_length").optional(),
  message: z.string().trim().min(10, "validation.message_min_length").max(2000, "validation.message_max_length"),
  service_type: z.string().optional(),
  location: z.string().trim().max(100, "validation.location_max_length").optional(),
  budget_range: z.string().optional(),
  timeline: z.string().optional(),
});

// Guest email/name validation schema
const guestSchema = z.object({
  guestEmail: z.string().email("validation.invalid_email").max(255),
  guestName: z.string().trim().max(100).optional(),
});

const initialFormData: ComposeFormData = {
  subject: '',
  message: '',
  service_type: '',
  location: '',
  budget_range: '',
  timeline: '',
};

export function useComposeForm() {
  const { lComponents } = useLocalizations();
  
  const [formData, setFormData] = useState<ComposeFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fieldStatus, setFieldStatus] = useState<Record<string, FieldStatus>>({});
  
  // Guest-specific state
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');

  const updateFormData = useCallback((updates: Partial<ComposeFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const handleFieldBlur = useCallback((field: string, value: string) => {
    if (field === 'subject') {
      // Subject is optional, only validate max length
      if (value.length > 200) {
        setErrors(prev => ({ ...prev, subject: lComponents('compose_message.validation.subject_max_length', { defaultValue: 'Subject is too long' }) }));
        setFieldStatus(prev => ({ ...prev, subject: 'error' }));
      } else {
        setErrors(prev => { const { subject: _, ...rest } = prev; return rest; });
        setFieldStatus(prev => ({ ...prev, subject: 'idle' }));
      }
    } else if (field === 'message') {
      if (!value.trim()) {
        setErrors(prev => ({ ...prev, message: lComponents('compose_message.validation.message_required', { defaultValue: 'Message is required' }) }));
        setFieldStatus(prev => ({ ...prev, message: 'error' }));
      } else if (value.trim().length < 10) {
        setErrors(prev => ({ ...prev, message: lComponents('compose_message.validation.message_min_length', { defaultValue: 'Message must be at least 10 characters' }) }));
        setFieldStatus(prev => ({ ...prev, message: 'error' }));
      } else if (value.length > 2000) {
        setErrors(prev => ({ ...prev, message: lComponents('compose_message.validation.message_max_length', { defaultValue: 'Message is too long' }) }));
        setFieldStatus(prev => ({ ...prev, message: 'error' }));
      } else {
        setErrors(prev => { const { message: _, ...rest } = prev; return rest; });
        setFieldStatus(prev => ({ ...prev, message: 'idle' }));
      }
    } else if (field === 'guestEmail') {
      // Validate guest email on blur
      const emailResult = z.string().email().safeParse(value);
      if (!value.trim()) {
        setErrors(prev => ({ ...prev, guestEmail: lComponents('compose_message.validation.email_required', { defaultValue: 'Email is required' }) }));
        setFieldStatus(prev => ({ ...prev, guestEmail: 'error' }));
      } else if (!emailResult.success) {
        setErrors(prev => ({ ...prev, guestEmail: lComponents('compose_message.validation.invalid_email', { defaultValue: 'Invalid email address' }) }));
        setFieldStatus(prev => ({ ...prev, guestEmail: 'error' }));
      } else {
        setErrors(prev => { const { guestEmail: _, ...rest } = prev; return rest; });
        setFieldStatus(prev => ({ ...prev, guestEmail: 'idle' }));
      }
    } else if (field === 'guestName') {
      // Name is optional, only validate max length
      if (value.length > 100) {
        setErrors(prev => ({ ...prev, guestName: lComponents('compose_message.validation.name_max_length', { defaultValue: 'Name is too long' }) }));
        setFieldStatus(prev => ({ ...prev, guestName: 'error' }));
      } else {
        setErrors(prev => { const { guestName: _, ...rest } = prev; return rest; });
        setFieldStatus(prev => ({ ...prev, guestName: 'idle' }));
      }
    }
  }, [lComponents]);

  const validateForm = useCallback((selectedExperts: Expert[], isGuest: boolean = false) => {
    const newErrors: Record<string, string> = {};
    
    // Validate guest fields if applicable
    if (isGuest) {
      const guestResult = guestSchema.safeParse({ guestEmail, guestName: guestName || undefined });
      if (!guestResult.success) {
        guestResult.error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
      }
    }
    
    // Validate message schema
    try {
      messageSchema.parse({
        recipients: selectedExperts.map(e => e.id),
        subject: formData.subject,
        message: formData.message,
        service_type: formData.service_type || undefined,
        location: formData.location || undefined,
        budget_range: formData.budget_range || undefined,
        timeline: formData.timeline || undefined,
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    
    setErrors({});
    return true;
  }, [formData, guestEmail, guestName]);

  const clearRecipientError = useCallback(() => {
    setErrors(prev => { const { recipients: _, ...rest } = prev; return rest; });
  }, []);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    setFieldStatus({});
    setGuestEmail('');
    setGuestName('');
  }, []);

  return {
    formData,
    errors,
    fieldStatus,
    guestEmail,
    guestName,
    setFormData,
    updateFormData,
    handleFieldBlur,
    validateForm,
    clearRecipientError,
    resetForm,
    setErrors,
    setGuestEmail,
    setGuestName,
  };
}
